import { useState, useEffect, useRef } from 'react';
import { X, Gift, ArrowDown, Camera, Bug } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CANCEL_REASONS = [
  'Trop cher',
  "Pas assez de contenus qui m'intéressent",
  'Problèmes techniques (lecture, bugs…)',
  "J'utilise une autre plateforme",
  'Je ne l\'utilise pas assez',
  'Autre',
];

const REASON_BUG = 'Problèmes techniques (lecture, bugs…)';

const fmtEur = (p) => `${Number(p).toFixed(2).replace('.', ',')} €`;

export function CancelSurveyModal({ onConfirm, onClose, busy, onSaved, winback }) {
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [step, setStep] = useState(winback ? 'loading' : 'survey'); // loading | survey | bug | offer | accepted
  const [offer, setOffer] = useState(null);
  const [offerBusy, setOfferBusy] = useState(false);
  const [bugDesc, setBugDesc] = useState('');
  const [bugPhotos, setBugPhotos] = useState([]);
  const [bugUploading, setBugUploading] = useState(false);
  const [bugSending, setBugSending] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!winback) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/subscribe/retention-offer`, { withCredentials: true });
        if (data.eligible) { setOffer(data); setStep('offer'); return; }
      } catch { /* pas d'offre */ }
      onClose();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = async () => {
    const r = reason || 'Non précisé';
    if (r === 'Trop cher') {
      setOfferBusy(true);
      try {
        const { data } = await axios.get(`${API}/subscribe/retention-offer`, { withCredentials: true });
        if (data.eligible) { setOffer(data); setStep('offer'); setOfferBusy(false); return; }
      } catch { /* pas d'offre → résiliation normale */ }
      setOfferBusy(false);
    }
    if (r === REASON_BUG) { setStep('bug'); return; }
    onConfirm(r, comment);
  };

  const addPhotos = async (files) => {
    const list = Array.from(files || []).slice(0, 3 - bugPhotos.length);
    if (!list.length) return;
    setBugUploading(true);
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await axios.post(`${API}/upload/image`, fd, { withCredentials: true });
        setBugPhotos(p => [...p, data.file_url]);
      } catch { alert("Échec de l'envoi d'une photo, réessayez."); }
    }
    setBugUploading(false);
  };

  const sendBugAndCancel = async () => {
    setBugSending(true);
    try {
      await axios.post(`${API}/support/bug-report`, { description: bugDesc, photos: bugPhotos }, { withCredentials: true });
    } catch { /* la résiliation continue quand même */ }
    setBugSending(false);
    onConfirm(REASON_BUG, bugDesc.slice(0, 500) || comment);
  };

  const acceptOffer = async () => {
    setOfferBusy(true);
    try {
      await axios.post(`${API}/subscribe/retention-offer/accept`, {}, { withCredentials: true });
      setStep('accepted');
    } catch (err) {
      alert(err.response?.data?.detail || "Impossible d'appliquer l'offre");
    } finally {
      setOfferBusy(false);
    }
  };

  const refuseOffer = () => {
    axios.post(`${API}/subscribe/retention-offer/decline`, {}, { withCredentials: true }).catch(() => {});
    if (winback) { onClose(); return; }
    onConfirm(reason || 'Non précisé', comment);
  };

  const closeAccepted = () => { onSaved?.(); onClose(); };

  return (
    <div data-portal="true" className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4" onClick={step === 'accepted' ? closeAccepted : onClose} data-testid="cancel-survey-modal">
      <div className="bg-[#1a0e2e] border border-white/10 rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>

        {step === 'loading' && (
          <p className="text-sm text-white/50 text-center py-6" data-testid="retention-loading">Chargement de votre offre…</p>
        )}

        {step === 'survey' && (
          <>
            <div className="flex items-start justify-between mb-1">
              <h3 className="text-lg font-bold text-white">Avant de partir…</h3>
              <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="cancel-survey-close"><X size={18} /></button>
            </div>
            <p className="text-xs text-white/45 mb-5">Pouvez-vous nous dire pourquoi vous résiliez ? Cela nous aide à améliorer Maya+.</p>
            <div className="space-y-1.5 mb-4">
              {CANCEL_REASONS.map((r, i) => (
                <button key={r} onClick={() => setReason(r)} data-testid={`cancel-reason-${i}`}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg border text-left text-sm transition-colors ${
                    reason === r ? 'border-[#E9641A] bg-[#E9641A]/10 text-white' : 'border-white/10 bg-white/[0.03] text-white/60 hover:border-white/25'}`}>
                  <span className={`w-3.5 h-3.5 rounded-full border-2 flex-none ${reason === r ? 'border-[#E9641A] bg-[#E9641A]' : 'border-white/30'}`} />
                  {r}
                </button>
              ))}
            </div>
            <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2} maxLength={500}
              placeholder="Un commentaire ? (optionnel)" data-testid="cancel-survey-comment"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#E9641A] resize-none mb-4" />
            <div className="flex flex-col gap-2">
              <button onClick={handleConfirm} disabled={busy || offerBusy} data-testid="cancel-survey-confirm"
                className="w-full py-2.5 rounded-lg text-sm font-bold text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 transition-colors disabled:opacity-50">
                {busy || offerBusy ? 'Un instant…' : 'Confirmer la résiliation'}
              </button>
              <button onClick={onClose} data-testid="cancel-survey-keep"
                className="w-full py-2.5 rounded-lg text-sm font-bold text-white bg-[#E9641A] hover:bg-[#FE975E] transition-colors">
                Garder mon abonnement
              </button>
            </div>
          </>
        )}

        {step === 'bug' && (
          <div data-testid="cancel-bug-step">
            <div className="flex items-start justify-between mb-1">
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><Bug size={18} className="text-[#E9641A]" /> Décrivez le problème</h3>
              <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="cancel-bug-close"><X size={18} /></button>
            </div>
            <p className="text-xs text-white/45 mb-4">Décrivez le bug rencontré (appareil, contenu, moment…). Notre assistant technique analyse votre problème et vous répond par email — souvent avec une solution immédiate.</p>
            <textarea value={bugDesc} onChange={e => setBugDesc(e.target.value)} rows={4} maxLength={2000} autoFocus
              placeholder="Ex : sur ma TV LG, l'épisode 2 de … se fige au bout de 5 minutes…" data-testid="cancel-bug-desc"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#E9641A] resize-none mb-3" />
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="cancel-bug-file-input"
              onChange={e => { addPhotos(e.target.files); e.target.value = ''; }} />
            <div className="flex items-center gap-2 flex-wrap mb-4">
              {bugPhotos.map((p, i) => (
                <div key={p} className="relative">
                  <img src={p} alt="" className="w-14 h-14 object-cover rounded-lg border border-white/15" data-testid={`cancel-bug-photo-${i}`} />
                  <button onClick={() => setBugPhotos(ph => ph.filter(x => x !== p))} data-testid={`cancel-bug-photo-remove-${i}`}
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center"><X size={10} /></button>
                </div>
              ))}
              {bugPhotos.length < 3 && (
                <button onClick={() => fileRef.current?.click()} disabled={bugUploading} data-testid="cancel-bug-add-photo"
                  className="w-14 h-14 rounded-lg border border-dashed border-white/25 text-white/40 hover:text-white hover:border-white/50 flex flex-col items-center justify-center transition-colors disabled:opacity-50">
                  <Camera size={16} />
                  <span className="text-[8px] mt-0.5">{bugUploading ? 'Envoi…' : 'Photo'}</span>
                </button>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={sendBugAndCancel} disabled={busy || bugSending || bugUploading || !bugDesc.trim()} data-testid="cancel-bug-submit"
                className="w-full py-2.5 rounded-lg text-sm font-bold text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 transition-colors disabled:opacity-50">
                {busy || bugSending ? 'Un instant…' : 'Envoyer et confirmer la résiliation'}
              </button>
              <button onClick={() => onConfirm(REASON_BUG, comment)} disabled={busy || bugSending} data-testid="cancel-bug-skip"
                className="w-full py-2 rounded-lg text-xs font-semibold text-white/45 hover:text-white transition-colors">
                Confirmer sans signaler le bug
              </button>
              <button onClick={onClose} data-testid="cancel-bug-keep"
                className="w-full py-2.5 rounded-lg text-sm font-bold text-white bg-[#E9641A] hover:bg-[#FE975E] transition-colors">
                Garder mon abonnement
              </button>
            </div>
          </div>
        )}

        {step === 'offer' && offer && (
          <div data-testid="retention-offer-modal">
            <div className="flex items-start justify-between mb-1">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                {offer.kind === 'discount' ? <Gift size={18} className="text-[#E9641A]" /> : <ArrowDown size={18} className="text-[#E9641A]" />}
                On vous fait un geste
              </h3>
              <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="retention-offer-close"><X size={18} /></button>
            </div>
            {offer.kind === 'discount' ? (
              <>
                <p className="text-sm text-white/70 mt-3 mb-4">Restez avec nous et profitez de <b className="text-white">−{offer.percent} % sur votre prochain prélèvement</b>.</p>
                <div className="rounded-lg border border-[#E9641A]/30 bg-[#E9641A]/10 p-4 mb-2 text-center">
                  <span className="text-white/40 line-through text-base mr-3">{fmtEur(offer.current_plan.price)}</span>
                  <span className="text-2xl font-extrabold text-[#E9641A]" data-testid="retention-discounted-price">{fmtEur(offer.discounted_price)}</span>
                  <span className="text-xs text-white/50 ml-1.5">{offer.current_plan.billing === 'annual' ? '/an' : '/mois'}</span>
                </div>
                <p className="text-[11px] text-white/40 mb-5">Remise appliquée automatiquement sur votre prochaine facture, une seule fois. Votre tarif habituel reprend ensuite.</p>
              </>
            ) : (
              <>
                <p className="text-sm text-white/70 mt-3 mb-4">Restez avec nous en passant à une offre plus abordable :</p>
                <div className="rounded-lg border border-[#E9641A]/30 bg-[#E9641A]/10 p-4 mb-2 text-center">
                  <p className="text-sm font-bold text-white mb-0.5">{offer.target_plan.name}</p>
                  <span className="text-white/40 line-through text-base mr-3">{fmtEur(offer.current_plan.price)}</span>
                  <span className="text-2xl font-extrabold text-[#E9641A]" data-testid="retention-target-price">{fmtEur(offer.target_plan.price)}</span>
                  <span className="text-xs text-white/50 ml-1.5">/mois</span>
                </div>
                <p className="text-[11px] text-white/40 mb-5">Changement immédiat, sans engagement. Vous pouvez rechanger d'offre à tout moment depuis votre compte.</p>
              </>
            )}
            <div className="flex flex-col gap-2">
              <button onClick={acceptOffer} disabled={offerBusy || busy} data-testid="retention-accept-btn"
                className="w-full py-2.5 rounded-lg text-sm font-bold text-white bg-[#E9641A] hover:bg-[#FE975E] transition-colors disabled:opacity-50">
                {offerBusy ? 'Application…' : "J'accepte l'offre"}
              </button>
              <button onClick={refuseOffer} disabled={offerBusy || busy} data-testid="retention-refuse-btn"
                className="w-full py-2.5 rounded-lg text-sm font-bold text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/25 transition-colors disabled:opacity-50">
                {winback ? 'Non merci' : (busy ? 'Résiliation…' : 'Non merci, résilier quand même')}
              </button>
            </div>
          </div>
        )}

        {step === 'accepted' && (
          <div className="text-center py-4" data-testid="retention-success">
            <div className="w-12 h-12 rounded-full bg-[#E9641A]/15 border border-[#E9641A]/40 flex items-center justify-center mx-auto mb-4">
              <Gift size={22} className="text-[#E9641A]" />
            </div>
            <h3 className="text-lg font-bold text-white mb-2">C'est noté, merci de rester !</h3>
            <p className="text-sm text-white/60 mb-6">
              {offer?.kind === 'discount'
                ? 'La remise de −50 % sera appliquée automatiquement sur votre prochain prélèvement.'
                : `Vous êtes maintenant sur le forfait ${offer?.target_plan?.name}.`}
              {winback && ' Votre abonnement continue : la résiliation est annulée.'}
            </p>
            <button onClick={closeAccepted} data-testid="retention-success-close"
              className="w-full py-2.5 rounded-lg text-sm font-bold text-white bg-[#E9641A] hover:bg-[#FE975E] transition-colors">
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
